
import { useEffect, useRef } from "react";
import { useBlogPosts } from "@/hooks/blog";
import { useUnifiedFilters } from "./useUnifiedFilters";
import { useInfiniteScrollLogic } from "./useInfiniteScrollLogic";
import { useFilterReset } from "./useFilterReset";
import { useBlogMetadata } from "./useBlogMetadata";
import { useCategoryFetch } from "./useCategoryFetch";
import { useErrorToast } from "./useErrorToast";
import { useFilterTracking } from "./useFilterTracking";
import { useCacheInit } from "./useCacheInit";
import { useLocation } from "react-router-dom";

export const useBlogPageLogic = () => {
  // Ottieni i parametri dall'URL
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const categoryParam = searchParams.get('category');
  const searchParam = searchParams.get('search');
  
  // Inizializza cache
  useCacheInit();
  
  // Effetto per impostare i filtri in base ai parametri URL
  useEffect(() => {
    console.log(`[useBlogPageLogic] Parametri URL: category=${categoryParam}, search=${searchParam}`);
  }, [categoryParam, searchParam]);

  // Configura funzionalità unificate di filtro (ricerca e categoria)
  const {
    categoryId: selectedCategoryId,
    categoryName: selectedCategoryName,
    searchTerm,
    searchInputValue,
    isLoading: isCategoryChanging,
    handleCategoryClick,
    handleSearchChange,
    handleResetFilters: resetUnifiedFilters,
    filtersChanged
  } = useUnifiedFilters({
    initialCategory: categoryParam ? parseInt(categoryParam, 10) : null,
    initialSearchTerm: searchParam || ''
  });
  
  // Riferimento per tracciare i cambiamenti di categoria
  const categoryChangeCount = useRef(0);
  
  // Incrementa il contatore quando cambia la categoria
  useEffect(() => {
    categoryChangeCount.current += 1;
  }, [selectedCategoryId]);
  
  // Recupera categorie
  const { categories } = useCategoryFetch();
  
  // Recupera post del blog con filtri di ricerca e categoria
  const { 
    posts, 
    loading, 
    error, 
    fetchMore,
    hasMore,
    loadingMore,
    refetch,
    totalPosts
  } = useBlogPosts({
    searchTerm,
    category: selectedCategoryId,
    initialPerPage: 9,
    subsequentPerPage: 6,
    forceRefreshSignal: categoryChangeCount.current,
    isCategoryChanging
  });
  
  // Gestisce funzionalità di reset filtri
  const hasPreviousFiltersRef = useRef(false);
  
  const handleResetFilters = () => {
    resetUnifiedFilters();
    refetch();
  };
  
  // Configura lo scroll infinito
  const { loaderRef } = useInfiniteScrollLogic({
    posts,
    loading,
    hasMore,
    loadingMore,
    fetchMore
  });
  
  // Gestisce tracciamento modifiche filtri
  useFilterTracking({
    searchTerm,
    selectedCategoryId,
    refetch
  });
  
  // Forza refetch quando cambiano i filtri
  useEffect(() => {
    if (filtersChanged.current) {
      refetch();
      filtersChanged.current = false;
    }
  }, [searchTerm, selectedCategoryId, refetch]);
  
  // Gestisce notifiche toast per errori
  useErrorToast(error);
  
  // Gestisce metadati pagina
  const { getPageMetadata } = useBlogMetadata({
    posts,
    selectedCategory: selectedCategoryName,
    searchTerm
  });

  // Aggiorna il nome della categoria quando cambiano le categorie disponibili
  useEffect(() => {
    if (selectedCategoryId && categories.length > 0) {
      const category = categories.find(cat => cat.id === selectedCategoryId);
      if (category && category.name) {
        console.log(`[useBlogPageLogic] Aggiornamento nome categoria: ${category.id} (${category.name})`);
      }
    }
  }, [selectedCategoryId, categories]);
  
  // Traccia se i filtri sono stati utilizzati
  useEffect(() => {
    if (searchTerm || selectedCategoryId) {
      hasPreviousFiltersRef.current = true;
    }
  }, [searchTerm, selectedCategoryId]);

  console.log(`Pagina blog - Filtri correnti: ricerca="${searchTerm}", categoryId=${selectedCategoryId}, categoryName="${selectedCategoryName}", categoryChangeCount=${categoryChangeCount}`);
  console.log(`Pagina blog - Post caricati: ${posts.length}, loading: ${loading}, hasMore: ${hasMore}`);

  return {
    // Correlati alla ricerca e categoria (unificati)
    searchInputValue,
    searchTerm,
    selectedCategoryId,
    selectedCategoryName,
    handleSearchChange,
    handleCategoryClick,
    categories,
    isCategoryChanging,
    isSearching: !!searchTerm,
    
    // Correlati ai post
    posts,
    loading,
    hasMore,
    loadingMore,
    
    // Scroll infinito
    loaderRef,
    
    // Filtri
    handleResetFilters,
    
    // SEO
    getPageMetadata,
  };
};
